import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { allArticles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug) || allArticles[0];

  const articleSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    image: article.headerImage,
    author: {
      '@type': 'Organization',
      name: 'PandabuySpreadsheet Team',
    },
    publisher: {
      '@type': 'Organization',
      name: 'PandabuySpreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
      },
    },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': import.meta.env.VITE_SITE_URL + '/guide/' + article.slug,
    },
  }), [article]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd schema={articleSchema} />
      <Navbar />
      <main>
        <article>
          <header className="flex flex-col lg:flex-row min-h-[400px] lg:min-h-[520px]">
            <div className="lg:w-1/2 relative overflow-hidden min-h-[280px] lg:min-h-[520px]">
              <img
                src={article.headerImage}
                alt={article.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-transparent"></div>
              <div className="absolute top-8 left-8">
                <span className="text-xs font-medium uppercase tracking-widest text-white bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  {article.tag}
                </span>
              </div>
            </div>

            <div className="lg:w-1/2 bg-background-50 flex items-center">
              <div className="px-8 md:px-12 lg:px-16 py-12 md:py-16">
                <nav className="mb-4 flex items-center gap-2 text-xs text-foreground-400">
                  <Link to="/" className="hover:text-primary-500 cursor-pointer transition-colors">Home</Link>
                  <span>/</span>
                  <span className="text-foreground-600">{article.tag}</span>
                </nav>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight text-balance">
                  {article.title}
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
                  {article.subtitle}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-400">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    Updated daily
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    PandabuySpreadsheet Team
                  </span>
                </div>
                <div className="mt-6">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Open PandabuySpreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </header>

          <div className="w-full px-6 md:px-10 py-16 md:py-24">
            <div className="max-w-3xl mx-auto">
              {article.sections.map((section, idx) => (
                <section key={idx} className={idx > 0 ? 'mt-12 md:mt-16' : ''}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {section.body}
                  </p>
                </section>
              ))}

              <section className="mt-12 md:mt-16 p-6 md:p-8 bg-accent-50 rounded-xl border border-accent-200/60">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
                  Key Takeaways
                </h2>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 flex-shrink-0"></i>
                    <span>PandabuySpreadsheet offers 14 curated categories covering every wardrobe need</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 flex-shrink-0"></i>
                    <span>Community-verified links ensure reliability and eliminate dead listings</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 flex-shrink-0"></i>
                    <span>Daily updates keep the spreadsheet fresh with the latest finds</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 flex-shrink-0"></i>
                    <span>Average savings of $300-$500 per seasonal shopping cycle</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 flex-shrink-0"></i>
                    <span>Free to browse with no account required — start shopping smarter today</span>
                  </li>
                </ul>
              </section>

              <section className="mt-12 md:mt-16">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Conclusion
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.conclusion}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Browse PandabuySpreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-foreground-300 text-foreground-700 font-semibold text-sm hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back to Homepage
                    <i className="ri-arrow-left-line"></i>
                  </Link>
                </div>
              </section>

              <section className="mt-16 pt-10 border-t border-background-200/60">
                <div className="flex flex-col items-center text-center">
                  <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                    Explore More Guides
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full max-w-3xl">
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors"
                    >
                      <i className="ri-home-line"></i>
                      Home
                    </Link>
                    <a
                      href={mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors"
                    >
                      <i className="ri-external-link-line"></i>
                      Main Store
                    </a>
                    {allArticles.filter(a => a.slug !== slug).slice(0, 4).map(a => (
                      <Link
                        key={a.slug}
                        to={`/guide/${a.slug}`}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors"
                      >
                        <i className="ri-article-line"></i>
                        {a.tag}
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}